import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  TextField,
  Chip,
  Stack,
  CircularProgress,
  MenuItem,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../../api/axios';
import toast from 'react-hot-toast';

/* ─────────────────────────────────────────────
   FIELD RENDERER
───────────────────────────────────────────── */
const Field = ({ field, value, editMode, onChange }) => {
  const commonProps = {
    fullWidth: true,
    label: field.label,
    name: field.name,
    value: value ?? '',
    onChange,
    disabled: !editMode,
    size: 'small',
    variant: 'outlined'
  };

  if (field.type === 'textarea') {
    return (
      <Grid item xs={12}>
        <TextField {...commonProps} multiline rows={2} />
      </Grid>
    );
  }

  if (field.type === 'select') {
    return (
      <Grid item xs={12} md={4}>
        <TextField {...commonProps} select>
          {(field.options || []).map(opt => (
            <MenuItem key={String(opt.value)} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} md={4}>
      <TextField {...commonProps} />
    </Grid>
  );
};

/* ─────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────── */
const Section = ({ title, children }) => (
  <>
    <Typography
      variant="subtitle1"
      sx={{ mt: 4, mb: 1, fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 1 }}
    >
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </>
);

/* ─────────────────────────────────────────────
   STATUS COLOR HELPER
───────────────────────────────────────────── */
const statusColor = (s) => {
  if (s === 'APPROVED')  return 'success';
  if (s === 'REJECTED')  return 'error';
  if (s === 'VERIFIED')  return 'info';
  return 'warning';
};

/* ─────────────────────────────────────────────
   FIELD SCHEMA — mirrors EmployeeOnboarding entity exactly
───────────────────────────────────────────── */
const useFieldSchema = () =>
  useMemo(() => [
    {
      title: 'Personal',
      fields: [
        { name: 'fullName',        label: 'Full Name' },
        { name: 'dateOfBirth',     label: 'Date of Birth' },
        { name: 'age',             label: 'Age' },
        {
          name: 'gender', label: 'Gender', type: 'select',
          options: [
            { label: 'Male',   value: 'MALE'   },
            { label: 'Female', value: 'FEMALE' },
            { label: 'Other',  value: 'OTHER'  }
          ]
        },
        { name: 'bloodGroup',      label: 'Blood Group' },
        { name: 'personalEmail',   label: 'Personal Email' },
        { name: 'dateOfJoining',   label: 'Date of Joining' },
        { name: 'currentContactNo',   label: 'Contact No' },
        { name: 'alternateContactNo', label: 'Alternate Contact No' }
      ]
    },
    {
      title: 'Family',
      fields: [
        { name: 'fatherName',   label: 'Father Name'   },
        { name: 'fatherMobile', label: 'Father Mobile' },
        { name: 'motherName',   label: 'Mother Name'   },
        { name: 'motherMobile', label: 'Mother Mobile' },
        {
          name: 'married', label: 'Married', type: 'select',
          options: [{ label: 'Yes', value: true }, { label: 'No', value: false }]
        },
        { name: 'spouseName',   label: 'Spouse Name'   },
        { name: 'spouseMobile', label: 'Spouse Mobile' }
      ]
    },
    {
      title: 'Identity',
      fields: [
        { name: 'aadhaarNumber',        label: 'Aadhaar Number'   },
        { name: 'panNumber',            label: 'PAN Number'       },
        { name: 'rationCardNumber',     label: 'Ration Card No'   },
        { name: 'voterIdNumber',        label: 'Voter ID'         },
        { name: 'drivingLicenseNumber', label: 'Driving License'  },
        { name: 'passportNumber',       label: 'Passport Number'  }
      ]
    },
    {
      title: 'Education',
      fields: [
        { name: 'tenthPercentage',         label: '10th %'                   },
        { name: 'highestQualification',    label: 'Highest Qualification'    },
        { name: 'universityInstitution',   label: 'University / Institution' },
        { name: 'yearOfPassing',           label: 'Year of Passing'          },
        { name: 'educationalQualification',label: 'Educational Qualification'},
        { name: 'technicalQualification',  label: 'Technical Qualification'  }
      ]
    },
    {
      title: 'Bank & Statutory',
      fields: [
        { name: 'bankName',          label: 'Bank Name'       },
        { name: 'bankAccountNumber', label: 'Account Number'  },
        { name: 'ifscCode',          label: 'IFSC Code'       },
        { name: 'branchAddress',     label: 'Branch Address'  },
        { name: 'micrCode',          label: 'MICR Code'       },
        { name: 'accountType',       label: 'Account Type'    },
        { name: 'pfUan',             label: 'PF UAN'          },
        { name: 'esicNumber',        label: 'ESIC Number'     }
      ]
    },
    {
      title: 'Address (Current)',
      fields: [
        { name: 'currentAddress', label: 'Current Address', type: 'textarea' },
        { name: 'currentCity',    label: 'City'    },
        { name: 'currentState',   label: 'State'   },
        { name: 'currentPincode', label: 'Pincode' }
      ]
    },
    {
      title: 'Address (Permanent)',
      fields: [
        { name: 'permanentAddress', label: 'Permanent Address', type: 'textarea' },
        { name: 'permanentCity',    label: 'City'       },
        { name: 'permanentState',   label: 'State'      },
        { name: 'permanentPincode', label: 'Pincode'    },
        { name: 'permanentContactNo', label: 'Contact No' }
      ]
    },
    {
      title: 'Experience',
      fields: [
        {
          name: 'fresher', label: 'Fresher', type: 'select',
          options: [{ label: 'Yes', value: true }, { label: 'No', value: false }]
        },
        { name: 'experienceYears',   label: 'Experience (Yrs)'       },
        { name: 'relevantExperience',label: 'Relevant Experience',    type: 'textarea' },
        { name: 'previousCompany',   label: 'Previous Company',       type: 'textarea' },
        { name: 'previousPosition',  label: 'Previous Position',      type: 'textarea' },
        { name: 'employmentFrom',    label: 'Employment From'         },
        { name: 'employmentTill',    label: 'Employment Till'         },
        { name: 'reasonForLeaving',  label: 'Reason for Leaving',     type: 'textarea' }
      ]
    },
    {
      title: 'DRA',
      fields: [
        {
          name: 'draQualified', label: 'DRA Qualified', type: 'select',
          options: [{ label: 'Yes', value: true }, { label: 'No', value: false }]
        },
        { name: 'draCertificateNumber', label: 'DRA Certificate Number' }
      ]
    },
    {
      title: 'Emergency Contacts',
      fields: [
        { name: 'emergencyContactName',     label: 'Contact 1 — Name'     },
        { name: 'emergencyContactRelation', label: 'Contact 1 — Relation' },
        { name: 'emergencyContactNumber',   label: 'Contact 1 — Number'   },
        { name: 'emergencyContactAddress',  label: 'Contact 1 — Address', type: 'textarea' },

        { name: 'emergencyContactName2',     label: 'Contact 2 — Name'     },
        { name: 'emergencyContactRelation2', label: 'Contact 2 — Relation' },
        { name: 'emergencyContactNumber2',   label: 'Contact 2 — Number'   },
        { name: 'emergencyContactAddress2',  label: 'Contact 2 — Address', type: 'textarea' },

        { name: 'emergencyContactName3',     label: 'Contact 3 — Name'     },
        { name: 'emergencyContactRelation3', label: 'Contact 3 — Relation' },
        { name: 'emergencyContactNumber3',   label: 'Contact 3 — Number'   },
        { name: 'emergencyContactAddress3',  label: 'Contact 3 — Address', type: 'textarea' }
      ]
    },
    {
      title: 'Medical & Legal',
      fields: [
        {
          name: 'medicalIssues', label: 'Medical Issues', type: 'select',
          options: [{ label: 'Yes', value: true }, { label: 'No', value: false }]
        },
        { name: 'medicalDetails', label: 'Medical Details', type: 'textarea' },
        {
          name: 'relativesInCompany', label: 'Relatives in Company', type: 'select',
          options: [{ label: 'Yes', value: true }, { label: 'No', value: false }]
        },
        {
          name: 'criminalRecord', label: 'Criminal Record', type: 'select',
          options: [{ label: 'Yes', value: true }, { label: 'No', value: false }]
        },
        { name: 'criminalDetails',  label: 'Criminal Details',  type: 'textarea' },
        { name: 'financialDefault', label: 'Financial Default', type: 'textarea' }
      ]
    },
    {
      title: 'Declaration',
      fields: [
        {
          name: 'declarationAccepted', label: 'Declaration Accepted', type: 'select',
          options: [{ label: 'Yes', value: true }, { label: 'No', value: false }]
        },
        { name: 'declarationDate', label: 'Declaration Date' }
      ]
    }
  ], []);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const HrEmployeeProfile = ({ employeeId, onStatusChange }) => {
  const [form,     setForm]     = useState({});
  const [remarks,  setRemarks]  = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const schema = useFieldSchema();

  /* ── LOAD ── */
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/onboarding/employee/${employeeId}`);
      setForm(res.data || {});
      setRemarks(res.data?.hrRemarks || '');
    } catch (e) {
      setError('Failed to load onboarding data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) load();
  }, [employeeId]);

  /* ── CHANGE ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    // normalize boolean strings from selects
    let v = value;
    if (value === 'true')  v = true;
    if (value === 'false') v = false;
    setForm(prev => ({ ...prev, [name]: v }));
  };

  /* ── SAVE (HR UPDATE) ── */
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/onboarding/hr/update/${employeeId}`, form);
      toast.success('Changes saved successfully');
      setEditMode(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /* ── CANCEL EDIT ── */
  const handleCancelEdit = () => {
    setEditMode(false);
    load(); // revert unsaved changes
  };

  /* ── HR ACTIONS ── */
  const verify = async () => {
    if (!remarks.trim()) { toast.error('Please enter remarks before verifying'); return; }
    try {
      await api.post(`/onboarding/verify/${employeeId}`, null, { params: { remarks } });
      toast.success('Marked as Verified');
      load();
      if (onStatusChange) onStatusChange();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Verify failed');
    }
  };

  const approve = async () => {
    if (!remarks.trim()) { toast.error('Please enter remarks before approving'); return; }
    try {
      await api.post(`/onboarding/approve/${employeeId}`, null, { params: { remarks } });
      toast.success('Onboarding Approved');
      load();
      if (onStatusChange) onStatusChange();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Approve failed');
    }
  };

  const reject = async () => {
    if (!remarks.trim()) { toast.error('Please enter remarks before rejecting'); return; }
    try {
      await api.post(`/onboarding/reject/${employeeId}`, null, { params: { remarks } });
      toast.success('Onboarding Rejected');
      load();
      if (onStatusChange) onStatusChange();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Reject failed');
    }
  };

  /* ── DOWNLOAD PDF ── */
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/onboarding/pdf/${employeeId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Onboarding_${employeeId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF download failed');
    }
  };

  /* ── DOWNLOAD DOCUMENTS ZIP ── */
  const downloadDocs = async () => {
    try {
      const res = await api.get(`/onboarding/documents/${employeeId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Documents_${employeeId}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Documents download failed');
    }
  };

  /* ── LOADING STATE ── */
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentStatus = form?.status;
  /* Show approve/reject on SUBMITTED or VERIFIED */
  const canApproveReject = currentStatus === 'SUBMITTED' || currentStatus === 'VERIFIED';
  /* Show verify only on SUBMITTED */
  const canVerify = currentStatus === 'SUBMITTED';

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        HR Onboarding Profile
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>

        {/* ── TOP BAR ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {form.fullName || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Submitted by: {form.submittedBy || '—'} &nbsp;|&nbsp; Date: {form.submittedAt ? new Date(form.submittedAt).toLocaleDateString() : '—'}
            </Typography>
            {form.approvedBy && (
              <Typography variant="body2" color="text.secondary">
                {currentStatus} by: {form.approvedBy} on {form.approvedAt ? new Date(form.approvedAt).toLocaleDateString() : '—'}
              </Typography>
            )}
          </Box>
          <Chip
            label={currentStatus || '—'}
            color={statusColor(currentStatus)}
            sx={{ fontWeight: 700, fontSize: '0.85rem', px: 1 }}
          />
        </Box>

        {/* ── ACTION BUTTONS ── */}
        <Stack direction="row" spacing={1.5} mt={3} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadPdf} size="small">
            Download PDF
          </Button>
          <Button variant="outlined" startIcon={<FolderZipIcon />} onClick={downloadDocs} size="small">
            Download Documents
          </Button>

          {!editMode ? (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditMode(true)} size="small">
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                size="small"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outlined" color="inherit" startIcon={<CancelIcon />} onClick={handleCancelEdit} size="small">
                Cancel
              </Button>
            </>
          )}
        </Stack>

        {/* ── HR REMARKS & STATUS ACTIONS ── */}
        <Box mt={3} sx={{ bgcolor: '#f8f9fa', p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            HR Remarks
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Enter your remarks here..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            size="small"
          />

          {(canVerify || canApproveReject) && (
            <Stack direction="row" spacing={1.5} mt={2} flexWrap="wrap" useFlexGap>
              {canVerify && (
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<VerifiedIcon />}
                  onClick={verify}
                  size="small"
                >
                  Mark as Verified
                </Button>
              )}
              {canApproveReject && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={approve}
                    size="small"
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={reject}
                    size="small"
                  >
                    Reject
                  </Button>
                </>
              )}
            </Stack>
          )}

          {form.hrRemarks && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontStyle: 'italic' }}>
              Last remarks: "{form.hrRemarks}"
            </Typography>
          )}
        </Box>

        {/* ── ALL SECTIONS ── */}
        {schema.map(section => (
          <Section key={section.title} title={section.title}>
            <Grid container spacing={2}>
              {section.fields.map(f => (
                <Field
                  key={f.name}
                  field={f}
                  value={form[f.name]}
                  editMode={editMode}
                  onChange={handleChange}
                />
              ))}
            </Grid>
          </Section>
        ))}

      </Paper>
    </Box>
  );
};

export default HrEmployeeProfile;